import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import store, { RootState } from "../../store";
import { selectUserRoles } from "../../store/auth-slice";
import { consultOrderActions } from "../../store/consultOrder-slice";
import { useVideoConsultPolling } from "../../hooks/useVideoConsultPolling";
import { videoConsultActions } from "../../store/videoConsult-slice";
import { getTokenUserId } from "../../utils/tokenUtils";
import { markJoined, parseSuppressionKey } from "../../utils/consultSuppression";
import VideoConsultAlert from "../VideoConsultAlert/VideoConsultAlert";
import FloatingVideoPanel from "../FloatingVideoPanel/FloatingVideoPanel";

// Host of the whole video-consult feature (mounted at app level, gated on
// isAuthenticated): discovery polling + incoming-call alert + the floating
// PiP panel. Must NOT return null while a call is live — the panel has to
// survive the incoming list emptying and route navigation.
const VideoConsultManager: React.FC = () => {
  const dispatch = useDispatch();
  const userRoles = useSelector(selectUserRoles);
  const token = useSelector((state: RootState) => state.auth.token);
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo
  );
  const activeConsult = useSelector(
    (state: RootState) => state.videoConsult.activeConsult
  );

  // Direct role checking for video consultation
  const canReceiveVideoConsults = userRoles.includes("PRODAJNO_MESTO_USER");

  // Get correct user ID from JWT token
  const consultantId = token ? getTokenUserId(token) : null;

  const { incomingConsults } = useVideoConsultPolling({
    enabled:
      canReceiveVideoConsults &&
      marketplaceInfo?.takesOrders === 1 &&
      !activeConsult, // pause discovery during a call
    consultantId: consultantId ?? undefined, // Convert null to undefined for type compatibility
    intervalMs: 10000, // 10 seconds
  });

  // Actual room join (Daily's joined-meeting): re-arm ITAM-2583 — belt and
  // braces with the click-arm in VideoConsultAlert (plan 6.1; a duplicate
  // dispatch with the same consultId is a no-op) — and set the ghost
  // suppression marker for the local-join -> webhook-persisted window.
  // Arm FIRST: markJoined is non-throwing by contract, but this order
  // guarantees a storage bug can never cost the consult-order flow.
  const handlePharmacistJoined = useCallback(() => {
    if (activeConsult) {
      dispatch(
        consultOrderActions.armConsultOrder({ consultId: activeConsult.id })
      );
      markJoined(activeConsult.id);
    }
  }, [activeConsult, dispatch]);

  // NOTE: no "clear list on call end" effect here — post-end ghosts are
  // excluded by the backend discovery predicate once the join webhook is
  // persisted, and by the suppression marker inside that window. A
  // manager-side clear would add nothing and reintroduce ordering hazards.

  // Cross-tab: when a SIBLING tab joins a consult, its markJoined write
  // fires this tab's `storage` event — drop the consult from our incoming
  // list right away instead of leaving a stale, joinable alert ringing for
  // up to one poll interval. Read-only toward storage (the poll-side
  // suppression filter stays the durable mechanism; this is the reactive
  // fast path — lost only in the in-memory fallback mode, where the poll
  // filter still applies). Marker REMOVALS (newValue null, e.g. another
  // tab's logout clearAll) are deliberately ignored: removal must never
  // resurrect anything.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (!e.newValue) return;
      const consultId = parseSuppressionKey(e.key);
      if (consultId === null) return;
      dispatch(videoConsultActions.suppressIncomingConsult(consultId));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [dispatch]);

  // Warn before unload during an active call. The handler reads the STORE at
  // event time, not the closure: the 401/403 interceptor dispatches
  // auth/logout (whose extraReducer clears activeConsult synchronously) and
  // then hard-redirects, racing React's effect cleanup — reading current
  // state disarms the guard before the forced redirect commits, so a forced
  // logout never pops the browser leave-page confirm.
  useEffect(() => {
    if (!activeConsult) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!store.getState().videoConsult.activeConsult) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [activeConsult]);

  if (!canReceiveVideoConsults) {
    return null;
  }

  return (
    <>
      {/* key = consult id: per-call UI state (size, position, isMinimized,
          isConfirmingEnd, hasJoined) must NOT leak into the next call — a
          call ended while minimized would otherwise leave the NEXT call
          minimized, and a remote end while the confirm modal is open would
          replay that stale modal. Remounting per consult id resets
          everything; unmount cleanup still destroys the frame gracefully
          (layout-effect cleanup runs before DOM removal). */}
      <FloatingVideoPanel
        key={activeConsult?.id ?? "idle"}
        onPharmacistJoined={handlePharmacistJoined}
      />
      {incomingConsults.length > 0 && !activeConsult && <VideoConsultAlert />}
    </>
  );
};

export default VideoConsultManager;
